import {CurrentRepositoryProvider} from '@github-ui/current-repository'
import {CurrentUserProvider} from '@github-ui/current-user'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type React from 'react'
import {useState} from 'react'

import {CommitErrorState} from './components/CommitErrorState'
import {CommitsLayoutPayloadContext} from './hooks/use-commits-app-payload'
import type {CommitsLayoutPayload} from './routes/commits-layout-route'
import type {CommitsBasePayload} from './shared/types'

/**
 * The App component is used to render content which should be present on _all_ routes within this app
 */
export function App(props: {children?: React.ReactNode}) {
  const payload = useRoutePayload<CommitsBasePayload>()
  const appPayload = useAppPayload<CommitsLayoutPayload>()

  return (
    <CommitsLayoutPayloadContext value={appPayload}>
      <AppWrapper payload={payload}>{props.children}</AppWrapper>
    </CommitsLayoutPayloadContext>
  )
}

/**
 * Renders the providers which are shared by all routes within this app. Data router routes render this
 * directly with the payload from their own route query; the shared values live on the layout route.
 */
export function AppWrapper({payload, children}: {payload: CommitsBasePayload; children?: React.ReactNode}) {
  const [repo] = useState(payload?.repo)
  const [user] = useState(payload?.currentUser)
  const [userNameDisplayConfiguration] = useState(payload?.userNameDisplayConfiguration)

  return (
    <ErrorBoundary critical fallback={<CommitErrorState />}>
      <CurrentUserProvider user={user}>
        <CurrentRepositoryProvider repository={repo}>
          <UserNameDisplayContextProvider value={userNameDisplayConfiguration ?? undefined}>
            {children}
          </UserNameDisplayContextProvider>
        </CurrentRepositoryProvider>
      </CurrentUserProvider>
    </ErrorBoundary>
  )
}
